import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Send, ChevronLeft } from 'lucide-react';
import { getTickets, getTicket, updateTicket, addTicketMessage } from '../../services/api';
import { toast } from 'sonner';

export const AdminTicketPanel = () => {
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState('');

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try { const res = await getTickets(); setTickets(res.data); } catch {}
  };

  const openTicket = async (t) => {
    try { const res = await getTicket(t.id); setActiveTicket(res.data); setTicketReply(''); } catch { setActiveTicket(t); }
  };

  const handleSend = async () => {
    if (!ticketReply.trim()) return;
    try {
      await addTicketMessage(activeTicket.id, ticketReply.trim());
      const res = await getTicket(activeTicket.id);
      setActiveTicket(res.data);
      setTicketReply('');
      loadTickets();
      toast.success('Yanıt gönderildi');
    } catch { toast.error('Gönderilemedi'); }
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-120px)]">
      {/* Ticket List */}
      <div className={`${activeTicket ? 'hidden lg:block lg:w-80 xl:w-96 flex-shrink-0' : 'w-full'} space-y-2 overflow-y-auto`}>
        {tickets.length > 0 ? tickets.map(t => (
          <div key={t.id} onClick={() => openTicket(t)}
            className={`bg-white rounded-2xl p-4 shadow-sm cursor-pointer transition-all hover:shadow-md ${activeTicket?.id === t.id ? 'ring-2 ring-[#6b6de3]' : ''}`}
            data-testid={`ticket-${t.id}`}>
            <div className="flex items-start justify-between mb-1">
              <p className="font-semibold text-sm text-gray-900 line-clamp-1">{t.subject}</p>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ml-2 ${t.status === 'answered' ? 'bg-green-100 text-green-600' : t.status === 'closed' ? 'bg-gray-100 text-gray-600' : 'bg-yellow-100 text-yellow-600'}`}>
                {t.status === 'answered' ? 'Yanıtlandı' : t.status === 'closed' ? 'Çözüldü' : t.status === 'pending' ? 'Beklemede' : 'Aktif'}
              </span>
            </div>
            <p className="text-xs text-[#8E8E93]">{t.user_name} {'•'} {new Date(t.created_at).toLocaleDateString('tr-TR')}</p>
            <p className="text-xs text-gray-500 line-clamp-1 mt-1">{t.message}</p>
          </div>
        )) : <div className="bg-white rounded-2xl p-8 text-center"><p className="text-[#8E8E93]">Destek talebi yok</p></div>}
      </div>

      {/* Ticket Detail */}
      {activeTicket && (
        <div className="flex-1 bg-white rounded-2xl shadow-sm flex flex-col min-w-0" data-testid="ticket-detail">
          <div className="p-4 border-b border-gray-100 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <button onClick={() => setActiveTicket(null)} className="lg:hidden p-1 rounded-lg hover:bg-gray-100"><ChevronLeft size={18} /></button>
                <h3 className="font-semibold text-gray-900 truncate">{activeTicket.subject}</h3>
              </div>
              <p className="text-xs text-[#8E8E93] mt-0.5">{activeTicket.user_name} {'•'} {activeTicket.user_phone} {'•'} {new Date(activeTicket.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Select value={activeTicket.status} onValueChange={async (v) => {
                try { await updateTicket(activeTicket.id, { status: v }); toast.success('Durum güncellendi'); loadTickets(); setActiveTicket(prev => ({ ...prev, status: v })); } catch { toast.error('Hata'); }
              }}>
                <SelectTrigger className="h-7 text-xs rounded-full border-0 bg-[#F2F2F7] w-32" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktif</SelectItem>
                  <SelectItem value="answered">Yanıtlandı</SelectItem>
                  <SelectItem value="pending">Beklemede</SelectItem>
                  <SelectItem value="closed">Çözüldü</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            <div className="flex justify-start">
              <div className="bg-[#F2F2F7] rounded-2xl rounded-tl-md px-4 py-3 max-w-[80%]">
                <p className="text-sm text-gray-900">{activeTicket.message}</p>
                <p className="text-[10px] text-[#8E8E93] mt-1">{activeTicket.user_name}</p>
              </div>
            </div>
            {(activeTicket.messages || []).map((m, i) => (
              <div key={i} className={`flex ${m.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                <div className={`rounded-2xl px-4 py-3 max-w-[80%] ${m.sender === 'admin' ? 'bg-[#6b6de3] text-white rounded-tr-md' : 'bg-[#F2F2F7] text-gray-900 rounded-tl-md'}`}>
                  <p className="text-sm">{m.text}</p>
                  <p className={`text-[10px] mt-1 ${m.sender === 'admin' ? 'text-blue-100' : 'text-[#8E8E93]'}`}>
                    {m.sender === 'admin' ? 'Admin' : activeTicket.user_name} {'•'} {m.created_at ? new Date(m.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : ''}
                  </p>
                </div>
              </div>
            ))}
            {activeTicket.admin_response && !(activeTicket.messages || []).length && (
              <div className="flex justify-end">
                <div className="bg-[#6b6de3] text-white rounded-2xl rounded-tr-md px-4 py-3 max-w-[80%]">
                  <p className="text-sm">{activeTicket.admin_response}</p>
                  <p className="text-[10px] text-blue-100 mt-1">Admin</p>
                </div>
              </div>
            )}
          </div>
          {activeTicket.status !== 'closed' && (
            <div className="p-4 border-t border-gray-100 flex gap-2">
              <input value={ticketReply} onChange={e => setTicketReply(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && ticketReply.trim()) { e.preventDefault(); handleSend(); } }}
                placeholder={'Yanıt yazın...'} className="flex-1 bg-[#F2F2F7] rounded-xl px-4 py-3 text-sm outline-none"
                data-testid="ticket-reply-input" />
              <Button disabled={!ticketReply.trim()} onClick={handleSend}
                className="bg-[#6b6de3] rounded-xl px-4" data-testid="ticket-send-btn">
                <Send size={16} />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
